export type ActionType =
  | "vacation_order"
  | "medical_order"
  | "termination_order"
  | "amendment_order"
  | "hiring_order"
  | "leave_application"
  | "amendment"
  | "annual_leave"
  | "unpaid_leave"
  | "medical_leave"
  | "resignation_letter"
  | "amendment_application"
  | "amendment_agreement"
  | "contract_draft"
  | "job_application"
  | "service_agreement_individual"
  | "service_agreement_msb"
  | "vehicle_rental"
  | "premises_lease"
  | "goods_supply"
  | "completion_act"
  | "vehicle_handover"
  | "premises_handover"
  | "timesheet_draft"
  | "unit_request"
  | "unit_request_fulfill"
  | "gph_payment"
  | "task_deadline_approaching"
  | "fsm_submission"
  | "data_change_request"
  | "personal_sheet_completion"
  | "personal_sheet_signature"
  | "advance"
  | "business_trip"
  | "business_trip_order"
  | "project_removal_request";

export type ActionRequired =
  | "upload_signed_order"
  | "approve_application"
  | "sign_document"
  | "acknowledge_order"
  | "approve_draft"
  | "approve_timesheet"
  | "approve_request"
  | "fulfill_request"
  | "issue_business_trip"
  | "settle_business_trip"
  | "issue_advance"
  | "settle_advance"
  | "unsettled_salary_advance"
  | "submit_for_signing"
  | "wait"
  | "revise_application"
  | "review_gph_payment"
  | "complete_task"
  | "review_fsm_submission"
  | "approve_data_change"
  | "approve_data_change_chief"
  | "fill_personal_sheet"
  | "sign_personal_sheet"
  | "approve_project_removal"
  | "close_medical_leave";

export type Priority = "low" | "normal" | "high" | "urgent";

export type SigningSourceType =
  | "contract_draft"
  | "job_application"
  | "hiring_order"
  | "annual_leave"
  | "unpaid_leave"
  | "medical_leave"
  | "resignation_letter"
  | "amendment_application"
  | "amendment_agreement"
  | "amendment_order"
  | "service_agreement_individual"
  | "service_agreement_msb"
  | "vehicle_rental"
  | "premises_lease"
  | "goods_supply"
  | "completion_act"
  | "vehicle_handover"
  | "premises_handover"
  | "salary_advance"
  | "business_trip_order";

export interface SignDocumentDetails {
  document_id: string;
  bytes_url: string | null;
  sigex_view_url: string;
  url: string;
  signing_status: 0 | 1 | 2;
  source_type: SigningSourceType;
  candidate_id?: number;
  job_application_url?: string;
  signer_type?: "organization" | "counter_signer";
  signing_mode?: "bilateral" | "unilateral";
  org_signer_iin?: string;
  counter_signer_iin?: string;
  advance_id?: number;
  business_trip_id?: number;
  order_id?: number;
  order_number?: number;
  destination?: string;
  business_trip_reason?: string;
  original_file_url?: string | null;
  signed_pdf_url?: string | null;
  preview_url?: string;
  sign_url?: string;
}

export interface OrderDetails {
  leave_type?: "annual" | "unpaid" | "medical" | "termination" | "unknown";
  leave_id?: number;
  worker_id: number | null;
  document_url?: string | null;
  order_pdf_url?: string | null;
  unsigned_pdf_url?: string | null;
  candidate_application_id?: number | null;
  order_url?: string | null;
  amendment_id?: number | null;
  amendment_status?: string | null;
  source_type?: string;
  business_trip_id?: number;
  order_number?: number;
  destination?: string;
  business_trip_reason?: string;
}

export interface LeaveApprovalDetails {
  leave_type: "annual" | "unpaid" | "medical" | "resignation";
  leave_id?: number;
  period_start: string | null;
  period_end: string | null;
  worker_id: number | null;
}

export interface AmendmentApprovalDetails {
  clause_section: string;
  worker_id: number | null;
}

export interface TimesheetApprovalDetails {
  year: number;
  month: number;
  entries_count: number;
  timesheet_id: number;
}

export interface ItemRequestActionDetails {
  request_id: number;
  item_name: string;
  quantity: number;
  request_status?: string;
}

export interface AwaitingSignatureDetails {
  source_type: SigningSourceType;
  signer_type: "organization";
  amendment_id: number;
}

export interface SubmitForSigningDetails {
  source_type: SigningSourceType;
}

export interface WaitDetails {
  source_type: "job_application" | "hiring_order";
  candidate_id?: number | null;
  job_application_url?: string | null;
  order_number?: number;
}

export interface ReviseApplicationDetails {
  application_review_note: string;
  leave_type?: "annual" | "unpaid" | "medical";
  start_date?: string;
  end_date?: string;
  days_count?: number | null;
  reason?: string;
  diagnosis?: string;
  clause_section?: string | null;
  effective_date?: string;
  last_working_day?: string;
}

export interface GPHPaymentReviewDetails {
  gph_payment_id: number;
  completion_act_id: number;
  year: number;
  month: number;
  contractor_name: string;
  gross_amount: string;
}

export interface TaskDeadlineDetails {
  task_id: number;
  project_id: number;
  project_name: string;
  task_priority: string;
  days_remaining: number;
  is_overdue: boolean;
}

export interface FSMSubmissionDetails {
  source_type: "fsm_submission";
  submission_id: string;
  task_id: number;
  task_title: string;
  project_id: number;
}

export interface DataChangeApprovalDetails {
  request_id: number;
  worker_id: number;
  requested?: {
    surname: string;
    name: string;
    father_name: string;
    national_id_number: string;
    national_id_issue_date: string;
    national_id_expiry_date: string;
    national_id_issued_by: string;
    has_file: boolean;
  };
  current?: {
    surname: string;
    name: string;
    father_name: string;
    national_id_number: string;
    national_id_issue_date: string;
    national_id_expiry_date: string;
    national_id_issued_by: string;
  };
  changed_sections?: string[];
  initiated_by?: "worker" | "hr";
  requested_changes?: Record<string, unknown>;
  current_values?: Record<string, unknown>;
  new_education_records?: unknown[];
  new_social_category_records?: unknown[];
  uploaded_files_urls?: Record<string, string | null>;
}

export interface AdvanceApprovalDetails {
  source_type: "advance" | "business_trip";
  advance_id?: number;
  business_trip_id?: number;
  kind?: "salary" | "employee";
  amount?: string;
  amount_mode?: "exact" | "percent";
  percent?: string | null;
  advance_month?: number | null;
  advance_year?: number | null;
  computed_net?: string | null;
  comment?: string;
  outstanding?: string;
  worker_name?: string;
  destination?: string;
  business_trip_reason?: string;
  document_number?: string;
}

export interface PersonalSheetFillDetails {
  source_type: "personal_sheet";
  next_step: "complete";
  draft_url?: string;
}

export interface PersonalSheetSignDetails {
  source_type: "personal_sheet";
  next_step: "sign";
  preview_url?: string;
  sign_url?: string;
}

export interface ProjectRemovalRequestDetails {
  request_id: number;
  project_id: number;
  project_name: string;
  action_type: "delete" | "archive";
  reason: string;
  initiated_by: string;
  initiated_by_id: number;
}

export type PendingActionDetails =
  | SignDocumentDetails
  | OrderDetails
  | LeaveApprovalDetails
  | AmendmentApprovalDetails
  | TimesheetApprovalDetails
  | ItemRequestActionDetails
  | AwaitingSignatureDetails
  | SubmitForSigningDetails
  | WaitDetails
  | ReviseApplicationDetails
  | GPHPaymentReviewDetails
  | TaskDeadlineDetails
  | FSMSubmissionDetails
  | DataChangeApprovalDetails
  | AdvanceApprovalDetails
  | PersonalSheetFillDetails
  | PersonalSheetSignDetails
  | ProjectRemovalRequestDetails;

export interface PendingActionItem {
  id: string | number;
  type: ActionType;
  action_required: ActionRequired;
  subject_ru: string;
  subject_kk: string;
  subject_en: string;
  worker_name: string;
  created_at: string;
  due_date: string | null;
  priority: Priority;
  status: string;
  details: PendingActionDetails;
}

export interface PendingActionsResponse {
  items: PendingActionItem[];
  summary: {
    total_count: number;
    by_type: Record<string, number>;
    by_action: Record<string, number>;
  };
}

export function isSignDocumentDetails(d: PendingActionDetails): d is SignDocumentDetails {
  return "document_id" in d && ("sigex_view_url" in d || "url" in d);
}

export function isOrderDetails(d: PendingActionDetails): d is OrderDetails {
  return (
    "worker_id" in d &&
    !("document_id" in d) &&
    !("period_start" in d) &&
    !("clause_section" in d) &&
    !("timesheet_id" in d) &&
    !("request_id" in d)
  );
}

export function isLeaveApprovalDetails(d: PendingActionDetails): d is LeaveApprovalDetails {
  return "period_start" in d;
}

export function isAmendmentApprovalDetails(d: PendingActionDetails): d is AmendmentApprovalDetails {
  return "clause_section" in d;
}

export function isTimesheetApprovalDetails(d: PendingActionDetails): d is TimesheetApprovalDetails {
  return "timesheet_id" in d;
}

export function isItemRequestDetails(d: PendingActionDetails): d is ItemRequestActionDetails {
  return "request_id" in d;
}

export function isAwaitingSignatureDetails(d: PendingActionDetails): d is AwaitingSignatureDetails {
  return "amendment_id" in d && "signer_type" in d && !("document_id" in d);
}

export function isSubmitForSigningDetails(d: PendingActionDetails): d is SubmitForSigningDetails {
  return "source_type" in d && !("document_id" in d) && !("candidate_id" in d || "order_number" in d) && !("amendment_id" in d) && !("submission_id" in d);
}

export function isReviseApplicationDetails(d: PendingActionDetails): d is ReviseApplicationDetails {
  return "application_review_note" in d && !("document_id" in d) && !("period_start" in d);
}

export function isGPHPaymentReviewDetails(d: PendingActionDetails): d is GPHPaymentReviewDetails {
  return "gph_payment_id" in d && "contractor_name" in d && "gross_amount" in d && "year" in d && "month" in d;
}

export function isWaitDetails(d: PendingActionDetails): d is WaitDetails {
  return (
    "source_type" in d &&
    !("document_id" in d) &&
    !("request_id" in d) &&
    ("candidate_id" in d || "order_number" in d)
  );
}

export function isTaskDeadlineDetails(d: PendingActionDetails): d is TaskDeadlineDetails {
  return "task_id" in d && "project_id" in d && "days_remaining" in d;
}

export function isFSMSubmissionDetails(d: PendingActionDetails): d is FSMSubmissionDetails {
  return "submission_id" in d && "task_id" in d;
}

export function isDataChangeApprovalDetails(d: PendingActionDetails): d is DataChangeApprovalDetails {
  return "request_id" in d && "worker_id" in d && ("requested" in d || "changed_sections" in d);
}

export function isAdvanceApprovalDetails(d: PendingActionDetails): d is AdvanceApprovalDetails {
  return "source_type" in d && (d.source_type === "advance" || d.source_type === "business_trip") && !("document_id" in d) && !("next_step" in d);
}

export function isPersonalSheetFillDetails(d: PendingActionDetails): d is PersonalSheetFillDetails {
  return "source_type" in d && d.source_type === "personal_sheet" && "next_step" in d && d.next_step === "complete";
}

export function isPersonalSheetSignDetails(d: PendingActionDetails): d is PersonalSheetSignDetails {
  return "source_type" in d && d.source_type === "personal_sheet" && "next_step" in d && d.next_step === "sign";
}

export function isProjectRemovalRequestDetails(d: PendingActionDetails): d is ProjectRemovalRequestDetails {
  return "request_id" in d && "project_id" in d && "action_type" in d;
}

